// frontend/src/components/supplier/CreateSupplierDialog.jsx
//
// THE "Create supplier" dialog — the one form a new Supplier record is typed
// into, wherever the merchant asks for it: Supplier setup's "Create supplier"
// button, and the Item setup Suppliers dialog (ItemSuppliersDialog.jsx), where
// the supplier the merchant wants may not exist yet. One form, so a supplier
// created beside an item has every field the one created on its own page has.
//
// Mount it to open it, unmount it to close it — the form is fresh on every
// mount. Its seeds come from the store and are shown, never assumed: country
// from the store's region, currency from the store's, and the planning fields
// on INHERIT / blank so a new supplier follows the store until the merchant
// says otherwise (pages/supplierPlanningColumns.js holds the payload rules).
//
// A refused save stays in the dialog with its message over the form — the
// merchant fixes the field, not a toast they may have missed. A success is
// reported through the app toast and `onCreated(row)`, in that
// order; the host decides what to do with the row (re-read a grid, pick it in
// a form). A coded refusal (an FX rate the provider could not serve, an ERP
// that owns suppliers) is translated by `translateRequestError` like every
// other write in the app.

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import { ICON, X } from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../hooks/useFormatters';
import { useCountries, useCurrencies } from '../../hooks/useReferenceData';
import { useStorePlanning } from '../../hooks/useStorePlanning';
import { useCreateSupplier } from '../../hooks/useSupplierMutations';
import { translateRequestError } from '../../queries/api';
import { REVIEW_FREQUENCIES, orderDayForCadence, requiresPreferredOrderDay } from '../../constants/planning';
import { INVENTORY_TARGET_MODE, INVENTORY_TARGET_MODES, isValidTargetDaysOfSupply } from '../../utils/inventoryTargetMode';
import { isValidLeadTimeDays } from '../../utils/leadTime';
import { INHERIT, modeLabel } from '../../utils/planningInherit';
import { defaultCountryFromStoreRegion } from '../../utils/country';
import { formatCurrencyLabel } from '../../utils/itemLabel';
import {
  addSupplierPlanningPayload,
  effectivePlanningOf,
  frequencyLabel,
} from '../../pages/supplierPlanningColumns';
import { orderDayLabel, schedulePreview } from '../../pages/supplierSchedule';
import '../../styles/dialog-form.css';
import { showToast } from '../../components/common/appToastBus';

/**
 * @param {object} props
 * @param {() => void} props.onClose  the merchant cancelled or closed the dialog
 * @param {(supplier: object) => void} props.onCreated  the server's new supplier
 *   row, after the success toast; the host closes the dialog itself
 */
export default function CreateSupplierDialog({ onClose, onCreated }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const { formatNumber, parseNumber } = useFormatters();
  const { data: countries, error: countriesError } = useCountries();
  const { data: currencies, error: currenciesError } = useCurrencies();
  const { planning: storePlanning, isError: storePlanningError } = useStorePlanning();
  const createMutation = useCreateSupplier();
  const storeCurrencyCode = selectedStore?.currency ?? null;

  // `null` until the merchant touches a field: the seeds are read off the
  // store and the ISO lists at render time, so a country list that arrives
  // after the dialog opened still seeds the picker.
  const blank = useCallback(() => ({
    name: '', contactName: '', email: '', phone: '',
    address1: '', address2: '', city: '', stateProvince: '', postalCode: '',
    country: defaultCountryFromStoreRegion(countries, selectedStore?.region_code),
    leadTime: '', inventoryTargetMode: INHERIT, targetDaysOfSupply: '',
    reviewFrequency: INHERIT, preferredOrderDay: null, deliversOnWeekend: false,
    defaultCurrency: storeCurrencyCode,
    defaultTaxRate: '',
    // A locked rate is meaningful only for a cross-currency supplier: the
    // model and serializer reject any non-null lock for a same-currency row.
    // Empty means "follow the cached ECB rate."
    fixedFxRate: '',
  }), [countries, selectedStore?.region_code, storeCurrencyCode]);
  const [draft, setDraft] = useState(null);
  const form = draft ?? blank();
  const patch = (update) => setDraft((prev) => {
    const base = prev ?? blank();
    return { ...base, ...(typeof update === 'function' ? update(base) : update) };
  });
  const [error, setError] = useState(null);
  const busy = createMutation.isPending;

  const dayLabel = (dayIndex, reviewFrequency) => orderDayLabel(t, dayIndex, reviewFrequency);

  // "Non-empty" is not "valid" for the two pickers: a seed the list cannot
  // offer would leave a blank picker over an enabled button.
  const daysInvalid = form.targetDaysOfSupply !== '' && !isValidTargetDaysOfSupply(form.targetDaysOfSupply);
  const leadTimeInvalid = form.leadTime !== '' && !isValidLeadTimeDays(form.leadTime);
  const ready = form.name.trim() !== ''
    && countries.some((c) => c.country_code === form.country)
    && currencies.some((c) => c.currency_code === form.defaultCurrency)
    && !!storePlanning
    && !daysInvalid
    && !leadTimeInvalid;

  const handleCreate = async () => {
    if (!ready) return;
    setError(null);
    const payload = {
      supplier_name: form.name.trim(),
      supplier_main_contact_name: form.contactName.trim(),
      supplier_email: form.email.trim(),
      supplier_phone: form.phone.trim(),
      supplier_address_line1: form.address1.trim(),
      supplier_address_line2: form.address2.trim(),
      supplier_city: form.city.trim(),
      supplier_state_province: form.stateProvince.trim(),
      supplier_postal_code: form.postalCode.trim(),
      supplier_country_code_id: form.country,
      ...addSupplierPlanningPayload(form, parseNumber),
      delivers_on_weekend: form.deliversOnWeekend,
      default_currency_id: form.defaultCurrency,
      default_tax_rate: form.defaultTaxRate === '' ? null : parseNumber(form.defaultTaxRate),
      // The locked-rate field is disabled and emptied for a same-currency
      // supplier; dropping it here too keeps a stale value off the wire.
      fixed_fx_rate: (
        form.fixedFxRate === '' || form.defaultCurrency === storeCurrencyCode
          ? null
          : parseNumber(form.fixedFxRate)
      ),
    };
    try {
      const data = await createMutation.mutateAsync(payload);
      showToast(t('supplierSetup.messages.addSuccess'), 'success');
      onCreated(data);
    } catch (err) {
      setError(translateRequestError(err, t, 'supplierSetup.messages.addFailed'));
    }
  };

  const effective = storePlanning ? effectivePlanningOf(form, storePlanning) : null;

  return (
    <Dialog open onClose={busy ? undefined : onClose} className="dialog-form">
      <DialogTitle>
        {t('supplierSetup.createSupplier')}
        <IconButton onClick={onClose} size="small" disabled={busy} aria-label={t('btn.close')}>
          <X size={ICON.xl} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {countriesError && <Alert severity="error">{t('supplierSetup.messages.countriesFailed')}</Alert>}
        {currenciesError && <Alert severity="error">{t('supplierSetup.messages.currenciesFailed')}</Alert>}
        {storePlanningError && (
          <Alert severity="error">{t('supplierSetup.messages.storePlanningFailedCreate')}</Alert>
        )}

        {/* Section: General */}
        <Typography className="dialog-form-section">{t('supplierSetup.sections.general')}</Typography>
        <Box className="form-row-2col">
          <TextField
            label={t('supplierSetup.columns.supplierName')}
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            size="small"
            autoFocus
            required
            className="flex-1"
            disabled={busy}
          />
          <TextField
            label={t('supplierSetup.columns.contactName')}
            value={form.contactName}
            onChange={(e) => patch({ contactName: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
        </Box>
        <Box className="form-row-2col">
          <TextField
            label={t('supplierSetup.columns.email')}
            value={form.email}
            onChange={(e) => patch({ email: e.target.value })}
            size="small"
            type="email"
            className="flex-1"
            disabled={busy}
          />
          <TextField
            label={t('supplierSetup.columns.phone')}
            value={form.phone}
            onChange={(e) => patch({ phone: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
        </Box>

        {/* Section: Address */}
        <Typography className="dialog-form-section">{t('supplierSetup.sections.address')}</Typography>
        <Box className="form-row-2col">
          <TextField
            label={t('supplierSetup.columns.address1')}
            value={form.address1}
            onChange={(e) => patch({ address1: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
          <TextField
            label={t('supplierSetup.columns.city')}
            value={form.city}
            onChange={(e) => patch({ city: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
        </Box>
        <Box className="form-row-2col">
          <TextField
            label={t('supplierSetup.columns.address2')}
            value={form.address2}
            onChange={(e) => patch({ address2: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
          <TextField
            label={t('supplierSetup.columns.postalCode')}
            value={form.postalCode}
            onChange={(e) => patch({ postalCode: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
        </Box>
        <Box className="form-row-2col">
          <TextField
            label={t('supplierSetup.columns.stateProvince')}
            value={form.stateProvince}
            onChange={(e) => patch({ stateProvince: e.target.value })}
            size="small"
            className="flex-1"
            disabled={busy}
          />
          <Autocomplete
            options={countries}
            value={countries.find((c) => c.country_code === form.country) ?? null}
            getOptionLabel={(o) => o.country_name}
            onChange={(_, v) => patch({ country: v?.country_code ?? null })}
            renderInput={(params) => (
              <TextField {...params} label={t('supplierSetup.columns.country')} size="small" required />
            )}
            size="small"
            className="flex-1"
            disabled={busy}
          />
        </Box>

        {/* Section: Ordering */}
        <Typography className="dialog-form-section">{t('supplierSetup.sections.ordering')}</Typography>
        {/* Planning overrides — lead time, inventory target, cadence — each
            defaults to the store's value (INHERIT / blank → null on the wire).
            Rendered only once the store defaults are known: an inherit option
            has to name them. */}
        {effective && (
          <>
            <Box className="form-row-2col">
              <TextField
                label={t('supplierSetup.columns.leadTime')}
                value={form.leadTime}
                onChange={(e) => patch({ leadTime: e.target.value.replace(/\D/g, '') })}
                size="small"
                error={leadTimeInvalid}
                slotProps={{
                  htmlInput: { inputMode: 'numeric' },
                  input: { endAdornment: <InputAdornment position="end">{t('common.days')}</InputAdornment> },
                }}
                helperText={leadTimeInvalid
                  ? t('supplierSetup.messages.leadTimeInvalid')
                  : t('common.storeSetting', { value: formatNumber(storePlanning.lead_time_days, 0) })}
                className="flex-1"
                disabled={busy}
              />
              <Box className="flex-1">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.deliversOnWeekend}
                      onChange={(e) => patch({ deliversOnWeekend: e.target.checked })}
                      size="small"
                      disabled={busy}
                    />
                  )}
                  label={t('supplierSetup.columns.weekendDelivery')}
                  slotProps={{ typography: { variant: 'body2' } }}
                />
              </Box>
            </Box>
            <Box className="form-row-2col">
              <TextField
                label={t('supplierSetup.columns.inventoryTargetMode')}
                value={form.inventoryTargetMode}
                onChange={(e) => patch({ inventoryTargetMode: e.target.value })}
                size="small"
                select
                helperText={t('supplierSetup.tooltips.inventoryTargetMode')}
                className="flex-1"
                disabled={busy}
              >
                <MenuItem value={INHERIT}>
                  {t('common.storeSetting', { value: modeLabel(t, storePlanning.inventory_target_mode) })}
                </MenuItem>
                {INVENTORY_TARGET_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>{modeLabel(t, mode)}</MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('supplierSetup.columns.daysOfSupply')}
                value={form.targetDaysOfSupply}
                onChange={(e) => patch({ targetDaysOfSupply: e.target.value.replace(/\D/g, '') })}
                size="small"
                disabled={busy || effective.inventory_target_mode !== INVENTORY_TARGET_MODE.DAYS_OF_SUPPLY}
                error={daysInvalid}
                slotProps={{
                  htmlInput: { inputMode: 'numeric' },
                  input: { endAdornment: <InputAdornment position="end">{t('common.days')}</InputAdornment> },
                }}
                helperText={daysInvalid
                  ? t('supplierSetup.messages.daysOfSupplyInvalid')
                  : t('common.storeSetting', { value: formatNumber(storePlanning.target_days_of_supply, 0) })}
                className="flex-1"
              />
            </Box>
            <Box className="form-row-2col">
              <TextField
                label={t('supplierSetup.columns.reviewFrequency')}
                value={form.reviewFrequency}
                onChange={(e) => {
                  const freq = e.target.value;
                  patch((prev) => ({
                    reviewFrequency: freq,
                    preferredOrderDay: orderDayForCadence(
                      freq === INHERIT ? null : freq, prev.preferredOrderDay,
                    ),
                  }));
                }}
                size="small"
                select
                helperText={t('supplierSetup.tooltips.reviewFrequency')}
                className="flex-1"
                disabled={busy}
              >
                <MenuItem value={INHERIT}>
                  {t('common.storeSetting', { value: frequencyLabel(t, storePlanning.review_frequency) })}
                </MenuItem>
                {REVIEW_FREQUENCIES.map((freq) => (
                  <MenuItem key={freq} value={freq}>{frequencyLabel(t, freq)}</MenuItem>
                ))}
              </TextField>
              {/* Shown under an effective anchored cadence; editable only when
                  the cadence is the supplier's own — an inherited cadence
                  brings the store's day with it. */}
              {requiresPreferredOrderDay(effective.review_frequency) && (
                <TextField
                  label={t('supplierSetup.columns.preferredOrderDay')}
                  value={effective.preferred_order_day}
                  onChange={(e) => patch({ preferredOrderDay: parseInt(e.target.value, 10) })}
                  size="small"
                  select
                  disabled={busy || form.reviewFrequency === INHERIT}
                  helperText={form.reviewFrequency === INHERIT
                    ? t('supplierSetup.orderDayFollowsFrequency')
                    : undefined}
                  className="flex-1"
                >
                  {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                    <MenuItem key={day} value={day}>
                      {dayLabel(day, effective.review_frequency)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            {/* Rendered for EVERY cadence, including Daily — which otherwise
                explains itself nowhere, because its order-day control is
                absent rather than disabled. Restates the EFFECTIVE pair. */}
            <Typography variant="caption" color="text.secondary">
              {schedulePreview(t, effective.review_frequency, effective.preferred_order_day)}
            </Typography>
          </>
        )}

        {/* Section: Financial */}
        <Typography className="dialog-form-section">
          {t('supplierSetup.sections.financial')}
        </Typography>
        {/* The locked-rate field is disabled when the supplier's currency is
            the store's — there is no FX to lock — and any typed value is
            cleared the moment the merchant picks the store's currency. */}
        <Box className="form-row-2col">
          <Autocomplete
            options={currencies}
            value={currencies.find((c) => c.currency_code === form.defaultCurrency) ?? null}
            getOptionLabel={formatCurrencyLabel}
            onChange={(_, v) => {
              const newCcy = v?.currency_code ?? null;
              patch((prev) => ({
                defaultCurrency: newCcy,
                fixedFxRate: newCcy && newCcy !== storeCurrencyCode ? prev.fixedFxRate : '',
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('supplierSetup.columns.defaultCurrency')}
                size="small"
                required
                helperText={
                  storeCurrencyCode && form.defaultCurrency && form.defaultCurrency !== storeCurrencyCode
                    ? t('supplierSetup.tooltips.fxWillFetch', { base: form.defaultCurrency, quote: storeCurrencyCode })
                    : t('supplierSetup.tooltips.defaultCurrency')
                }
              />
            )}
            size="small"
            className="flex-1"
            disabled={busy}
          />
          <TextField
            label={t('supplierSetup.columns.fixedFxRate')}
            value={form.fixedFxRate}
            onChange={(e) => patch({ fixedFxRate: e.target.value.replace(/[^0-9.,]/g, '') })}
            size="small"
            disabled={busy || !form.defaultCurrency || form.defaultCurrency === storeCurrencyCode}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            helperText={t('supplierSetup.tooltips.fixedFxRate')}
            className="flex-1"
          />
          <TextField
            label={t('supplierSetup.columns.defaultTaxRate')}
            value={form.defaultTaxRate}
            onChange={(e) => patch({ defaultTaxRate: e.target.value.replace(/[^0-9.,]/g, '') })}
            size="small"
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
            }}
            className="flex-1"
            disabled={busy}
          />
        </Box>
        <DialogErrorBanner error={error} onClose={() => setError(null)} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>{t('btn.cancel')}</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!ready || busy} loading={busy}>
          {t('supplierSetup.createSupplier')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
